import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Sliders, Star, ArrowUpDown, RefreshCw } from "lucide-react";
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Empty } from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { SkeletonRows } from "@/components/ui/skeleton";
import { api, type MoverRow, type FavoriteRow } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/cn";

const WINDOWS = [7, 15, 30, 45, 60, 90] as const;
type Window = (typeof WINDOWS)[number];
type Direction = "abs" | "up" | "down";
const SIZES = [5, 10, 20, 50, 100] as const;

export function TopMovers() {
  const { t, fmtNumber } = useI18n();
  const { wallet } = useAuth();

  const [days, setDays] = useState<Window>(30);
  const [direction, setDirection] = useState<Direction>("abs");
  const [n, setN] = useState<number>(20);
  const [minStartPrice, setMinStartPrice] = useState("1");
  const [minDistinctDays, setMinDistinctDays] = useState("2");
  const [minMedianPrice, setMinMedianPrice] = useState("0.5");
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [data, setData] = useState<{ rows: MoverRow[]; count: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const refreshFavorites = useCallback(async () => {
    if (!wallet) {
      setFavorites(new Set());
      return;
    }
    try {
      const r = await api<{ items: FavoriteRow[] }>("/api/me/favorites");
      setFavorites(new Set((r.items || []).map((f) => f.card_id)));
    } catch {
      setFavorites(new Set());
    }
  }, [wallet]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      await refreshFavorites();
      const u = new URL("/api/top-movers", window.location.origin);
      u.searchParams.set("days", String(days));
      u.searchParams.set("n", String(n));
      u.searchParams.set("direction", direction);
      u.searchParams.set("min_start_price", minStartPrice);
      u.searchParams.set("min_distinct_days", minDistinctDays);
      u.searchParams.set("min_median_price", minMedianPrice);
      const r = await api<{ rows: MoverRow[]; count: number }>(u.toString());
      setData(r);
    } catch (e) {
      setErr((e as Error).message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, direction, n, minStartPrice, minDistinctDays, minMedianPrice, refreshFavorites]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.rows ?? [];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-fg">
            <ArrowUpDown className="h-3.5 w-3.5" />
            {t("movers.kicker") || "Movers"}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("movers.title") || "Top movers"}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-fg">
            {t("movers.subtitle") ||
              "Cards whose daily median price (all EUR sources combined) moves the most over the selected window."}
          </p>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t("movers.filters") || "Filters"}</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => load()} disabled={loading}>
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            {t("movers.load") || "Reload"}
          </Button>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t("movers.window") || "Window"}>
              <Select value={days} onChange={(v) => setDays(Number(v) as Window)}>
                {WINDOWS.map((w) => (
                  <option key={w} value={w}>
                    {w} {t("movers.days") || "days"}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("movers.direction") || "Direction"}>
              <Select value={direction} onChange={(v) => setDirection(v as Direction)}>
                <option value="abs">{t("movers.direction.abs") || "Absolute"}</option>
                <option value="up">{t("movers.direction.up") || "Up only"}</option>
                <option value="down">{t("movers.direction.down") || "Down only"}</option>
              </Select>
            </Field>
            <Field label={t("movers.top_n") || "Top N"}>
              <Select value={n} onChange={(v) => setN(Number(v))}>
                {SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
            <Button variant="outline" size="sm" onClick={() => setAdvancedOpen((o) => !o)}>
              <Sliders className="h-3.5 w-3.5" />
              {t("movers.advanced_filters") || "Advanced"}
            </Button>
          </div>
          {advancedOpen ? (
            <div className="grid grid-cols-1 gap-3 rounded-md border border-border bg-surface-2/40 p-3 sm:grid-cols-3">
              <Field label={`${t("movers.min_start_price") || "Start price ≥"} (€)`}>
                <Numeric value={minStartPrice} onChange={setMinStartPrice} step="0.1" />
              </Field>
              <Field label={t("movers.min_distinct_days") || "Distinct days ≥"}>
                <Numeric value={minDistinctDays} onChange={setMinDistinctDays} step="1" />
              </Field>
              <Field label={`${t("movers.min_median_price") || "Window median ≥"} (€)`}>
                <Numeric value={minMedianPrice} onChange={setMinMedianPrice} step="0.1" />
              </Field>
            </div>
          ) : null}
          {err ? <p className="text-xs text-danger">{err}</p> : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div>
            <CardTitle>
              {data ? `${data.count} ${t("movers.results") || "results"}` : t("movers.results") || "Results"}
            </CardTitle>
            <CardDescription>
              {direction === "abs"
                ? t("movers.subtitle.abs") || "Sorted by absolute price movement."
                : direction === "up"
                  ? t("movers.subtitle.up") || "Cards going up."
                  : t("movers.subtitle.down") || "Cards going down."}
            </CardDescription>
          </div>
          <Badge tone="info" variant="soft">
            {days}d window
          </Badge>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-hidden">
            <Table>
              <THead>
                <TR>
                  <TH className="w-10 text-right">#</TH>
                  <TH>{t("movers.col.card") || "Card"}</TH>
                  <TH>{t("movers.col.name") || "Name"}</TH>
                  <TH className="text-xs">{t("movers.col.period") || "Period"}</TH>
                  <TH className="text-right">{t("movers.col.start") || "Start"} €</TH>
                  <TH className="text-right">{t("movers.col.end") || "End"} €</TH>
                  <TH className="text-right">Δ €</TH>
                  <TH className="text-right">Δ %</TH>
                  <TH className="text-right">{t("movers.col.median") || "Median"} €</TH>
                  <TH className="text-right">{t("movers.col.days") || "Days"}</TH>
                  <TH className="text-right">{t("movers.col.points") || "Pts"}</TH>
                </TR>
              </THead>
              <TBody>
                {loading && rows.length === 0 ? (
                  <SkeletonRows rows={6} cols={11} />
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={11}>
                      <Empty
                        title={t("movers.no_results") || "No movers found"}
                        description={t("movers.no_results.desc") || "Try a wider window or loosen the filters."}
                      />
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => <MoverRowEl key={r.card_id} r={r} fav={favorites.has(r.card_id)} fmt={fmtNumber} />)
                )}
              </TBody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function MoverRowEl({
  r,
  fav,
  fmt,
}: {
  r: MoverRow;
  fav: boolean;
  fmt: (v: number | string | null | undefined, opts?: Intl.NumberFormatOptions) => string;
}) {
  const pct = Number(r.pct_change);
  const tone: "success" | "danger" | "neutral" = pct > 0 ? "success" : pct < 0 ? "danger" : "neutral";
  const ArrowIcon = pct > 0 ? TrendingUp : pct < 0 ? TrendingDown : null;
  const sign = pct > 0 ? "+" : "";

  return (
    <TR>
      <TD className="text-right text-xs text-muted-fg">{r.rank}</TD>
      <TD>
        <div className="flex items-center gap-1.5">
          {fav ? <Star className="h-3 w-3 fill-warn text-warn" /> : null}
          <Link
            to={`/card/${encodeURIComponent(r.card_id)}`}
            className="font-mono text-xs text-primary hover:underline"
          >
            {r.card_id}
          </Link>
        </div>
      </TD>
      <TD className="max-w-[220px] truncate">{r.card_name ?? "—"}</TD>
      <TD className="text-xs text-muted-fg">
        {r.start_day?.slice(0, 10)} → {r.end_day?.slice(0, 10)}
      </TD>
      <TD className="text-right font-mono tabular-nums">{fmt(r.start_price_eur, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TD>
      <TD className="text-right font-mono tabular-nums">{fmt(r.end_price_eur, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TD>
      <TD className="text-right">
        <Badge tone={tone} variant="soft" className="font-mono tabular-nums">
          {ArrowIcon ? <ArrowIcon className="h-3 w-3" /> : null}
          {sign}{fmt(r.abs_change_eur, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Badge>
      </TD>
      <TD className="text-right">
        <Badge tone={tone} variant="soft" className="font-mono tabular-nums">
          {sign}{Number(pct).toFixed(1)} %
        </Badge>
      </TD>
      <TD className="text-right font-mono text-muted-fg tabular-nums">{fmt(r.median_price_eur, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TD>
      <TD className="text-right text-xs text-muted-fg">{r.distinct_days}</TD>
      <TD className="text-right text-xs text-muted-fg">{r.total_points}</TD>
    </TR>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-muted-fg">{label}</span>
      {children}
    </label>
  );
}

function Select({
  value,
  onChange,
  children,
}: {
  value: string | number;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2.5 text-sm focus-visible:border-ring/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20"
    >
      {children}
    </select>
  );
}

function Numeric({
  value,
  onChange,
  step,
}: {
  value: string;
  onChange: (v: string) => void;
  step: string;
}) {
  return (
    <input
      type="number"
      value={value}
      step={step}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-3 text-sm focus-visible:border-ring/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20"
    />
  );
}
